/**
 * Team Inbox — "Add to existing" picker (item 3 — two tabs: Contacts | Leads).
 *
 * A searchable modal over the ALREADY-LOADED contacts AND leads streams (no
 * extra Firestore round-trip — reuses the `listenClients` + `listenLeads` arrays
 * the inbox already holds). Selecting a Contact LINKS the channel identity onto
 * that contact; selecting a Lead links it onto the lead (and routes the
 * conversation to it). Both resolve the triage draft so no duplicate lingers
 * (Unified Comms Hub: one identity, channels = labels).
 *
 * Pure search/match logic lives in lib/attachExisting.ts (unit-tested); this is
 * the thin presentation shell.
 */
import { useMemo, useState } from 'react';
import type { LiveClient } from '../../../lib/projectStore';
import type { Lead } from '../../../lib/leads';
import {
  contactHasEmail,
  contactHasPhone,
  filterContactsForPicker,
  filterLeadsForPicker,
  leadHasEmail,
  leadHasPhone,
} from '../lib/attachExisting';

type Tab = 'contacts' | 'leads';

export default function ContactPicker({
  channel,
  identityLabel,
  contacts,
  leads = [],
  busy = false,
  onPick,
  onPickLead,
  onClose,
}: {
  /** Which identity is being attached (drives copy + the already-linked hint). */
  channel: 'email' | 'whatsapp';
  /** The raw identity being linked (an email address or a phone number). */
  identityLabel: string;
  /** The already-loaded contacts stream (listenClients). */
  contacts: LiveClient[];
  /** The already-loaded leads stream (listenLeads). */
  leads?: Lead[];
  /** A link write is in flight. */
  busy?: boolean;
  /** Link this triage item to the chosen CONTACT. */
  onPick: (client: LiveClient) => void;
  /** Link this triage item to the chosen LEAD. */
  onPickLead?: (lead: Lead) => void;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<Tab>('contacts');
  const [q, setQ] = useState('');

  const contactResults = useMemo(
    () => filterContactsForPicker(contacts, q, 40),
    [contacts, q],
  );
  const leadResults = useMemo(() => filterLeadsForPicker(leads, q, 40), [leads, q]);

  const contactLinked = (c: LiveClient) =>
    channel === 'email'
      ? contactHasEmail(c, identityLabel)
      : contactHasPhone(c, identityLabel);
  const leadLinked = (l: Lead) =>
    channel === 'email' ? leadHasEmail(l, identityLabel) : leadHasPhone(l, identityLabel);

  return (
    <div className="ti-modal-backdrop" role="dialog" aria-modal aria-label="Add to existing">
      <div className="ti-picker">
        <div className="ti-compose-head">
          <h3>Add to existing</h3>
          <button
            type="button"
            className="ti-compose-x"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="ti-picker-sub">
          Link{' '}
          <b>
            {channel === 'email' ? '✉ ' : '🟢 '}
            {identityLabel}
          </b>{' '}
          to someone already in AVBP — searchable across{' '}
          <b>Contacts and Leads</b>. No duplicate is created.
        </div>

        <div className="ti-ptabs" role="tablist" aria-label="Pick source">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'contacts'}
            className={`ti-ptab${tab === 'contacts' ? ' on' : ''}`}
            onClick={() => setTab('contacts')}
          >
            Contacts
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'leads'}
            className={`ti-ptab${tab === 'leads' ? ' on' : ''}`}
            onClick={() => setTab('leads')}
          >
            Leads
          </button>
        </div>

        <input
          className="ti-picker-search"
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, C#, phone or email…"
          aria-label="Search"
        />

        <div className="ti-picker-list" role="listbox">
          {tab === 'contacts' ? (
            contactResults.length === 0 ? (
              <div className="ti-picker-empty">
                {q.trim() ? 'No contact matches that search.' : 'No contacts yet.'}
              </div>
            ) : (
              contactResults.map((c) => {
                const linked = contactLinked(c);
                return (
                  <button
                    key={c.cid}
                    type="button"
                    role="option"
                    className="ti-picker-row"
                    disabled={busy}
                    onClick={() => onPick(c)}
                  >
                    <span className="ti-picker-row-main">
                      <span className="ti-picker-row-name">
                        {c.name || '(no name)'}{' '}
                        <span className="ti-picker-row-cno">{c.clientNo}</span>
                      </span>
                      <span className="ti-picker-row-sub">
                        {c.phone || (c.phones && c.phones[0]?.number) || ''}
                        {(c.phone || (c.phones && c.phones[0]?.number)) &&
                        (c.email || (c.emails && c.emails[0]?.address))
                          ? ' · '
                          : ''}
                        {c.email || (c.emails && c.emails[0]?.address) || ''}
                      </span>
                    </span>
                    {linked && (
                      <span className="ti-picker-linked" title="Already linked">
                        ✓ linked
                      </span>
                    )}
                  </button>
                );
              })
            )
          ) : leadResults.length === 0 ? (
            <div className="ti-picker-empty">
              {q.trim() ? 'No lead matches that search.' : 'No leads yet.'}
            </div>
          ) : (
            leadResults.map((l) => {
              const linked = leadLinked(l);
              return (
                <button
                  key={l.id}
                  type="button"
                  role="option"
                  className="ti-picker-row"
                  disabled={busy || !onPickLead}
                  onClick={() => onPickLead?.(l)}
                >
                  <span className="ti-picker-row-main">
                    <span className="ti-picker-row-name">
                      {l.name || '(no name)'}{' '}
                      <span className="ti-picker-row-cno ti-lead">lead</span>
                    </span>
                    <span className="ti-picker-row-sub">
                      {l.phone || ''}
                      {l.phone && l.email ? ' · ' : ''}
                      {l.email || ''}
                      {l.location ? ` · ${l.location}` : ''}
                    </span>
                  </span>
                  {linked && (
                    <span className="ti-picker-linked" title="Already linked">
                      ✓ linked
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
